import { Workbox } from 'workbox-window';

/**
 * Production PWA update flow: no auto-reload.
 * When a new service worker is waiting, we show an "Update available" banner.
 * Reload only when the user clicks "Update App".
 */
export function register() {
  if ('serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL || window.location.origin, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Service worker won't work if PUBLIC_URL is on a different origin
      return;
    }

    window.addEventListener('load', async () => {
      // Prevent multiple registrations (guard against loops)
      if (window.__SW_REGISTERING) {
        return;
      }
      window.__SW_REGISTERING = true;

      // Do NOT unregister SW on version change – it can cause controller change and unexpected
      // reloads (Safari PWA / iOS). Let the browser handle SW updates via normal update flow.

      // Stable script URL is required: `register(...?v=27)` vs `...?v=30` are different URLs, so the
      // browser / Workbox can treat updates as "external" and Safari may never hand control to the new worker.
      // Bust caches with `updateViaCache: 'none'` and re-fetch via `registration.update()` instead.
      const publicUrl = process.env.PUBLIC_URL || window.location.origin;
      const swUrl = `${publicUrl}/service-worker-workbox.js`;

      // Verify the SW script is reachable (many hosts return 405 for HEAD on static JS — use GET).
      try {
        const response = await fetch(swUrl, { method: 'GET', cache: 'no-cache' });
        if (!response.ok) {
          return;
        }
      } catch (fetchError) {
        return;
      }

      try {
        const wb = new Workbox(swUrl, { updateViaCache: 'none' });

        // Register the service worker
        const registration = await wb.register();

        const dispatchUpdateBanner = () => {
          window.dispatchEvent(new CustomEvent('swUpdateReady'));
        };

        // If a worker is already in "waiting" (e.g. update finished before we attached listeners), show banner
        if (registration.waiting) {
          dispatchUpdateBanner();
        }

        // New SW is waiting – notify app to show "Update available" banner (no auto-activate)
        wb.addEventListener('waiting', dispatchUpdateBanner);

        // updatefound → installing → installed: if old SW still controls, new one becomes "waiting" (event can race)
        registration.addEventListener('updatefound', () => {
          const w = registration.installing;
          if (!w) return;
          w.addEventListener('statechange', () => {
            if (w.state === 'installed' && registration.waiting) {
              dispatchUpdateBanner();
            }
          });
        });

        // When the new SW takes control (after skipWaiting), reload the page.
        // This listener is registered once on page load so it never misses the event,
        // even if applyUpdate() is called from another component later (e.g. splash).
        wb.addEventListener('controlling', () => {
          if (window.__SW_USER_REQUESTED_RELOAD) {
            window.__SW_USER_REQUESTED_RELOAD = false;
            window.location.reload();
          }
        });

        // Check for new SW when user returns to tab (so we can show "Update available" banner).
        // Safe: we no longer auto-reload; user must click "Update App".
        const handleVisibilityChange = () => {
          if (document.visibilityState === 'visible') {
            wb.update().catch(() => {});
          }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', () => {
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        });
      } catch (error) {
        // If registration fails, try to clean up and retry once
        if (error.message.includes('invalid state') || error.message.includes('Unknown') || error.message.includes('fetch')) {
          try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map(reg => reg.unregister()));
          } catch (cleanupError) {
            // Cleanup failed
          }
        }
      }
    });
  }
}

/**
 * Call when user clicks "Update App" in the banner. Activates the waiting SW, then reloads
 * exactly once on the first of: controllerchange, the waiting worker reaching 'activated',
 * or a 10 s last-resort watchdog. SKIP_WAITING is retried because iOS Safari can drop a
 * single postMessage to a suspended waiting worker (the cause of the banner reappearing
 * after an "update" that never actually applied).
 */
export function applyUpdate() {
  // Guard: if already requested, don't set up duplicate listeners/timers.
  if (window.__SW_USER_REQUESTED_RELOAD) return;
  window.__SW_USER_REQUESTED_RELOAD = true;

  const reloadOnce = () => {
    if (!window.__SW_USER_REQUESTED_RELOAD) return; // already handled
    window.__SW_USER_REQUESTED_RELOAD = false;
    window.location.reload();
  };

  // Native controllerchange — fires when the new SW takes control (clientsClaim in the SW).
  // More reliable on iOS Safari than Workbox's 'controlling' event.
  try {
    navigator.serviceWorker.addEventListener('controllerchange', reloadOnce, { once: true });
  } catch (_) {}

  navigator.serviceWorker.getRegistration().then((reg) => {
    const waiting = reg && reg.waiting;
    if (!waiting) {
      // Nothing waiting (already activated, or stale banner) — just reload.
      reloadOnce();
      return;
    }

    // Reload the moment the waiting worker actually activates, even if
    // controllerchange is missed (a known Safari flake).
    waiting.addEventListener('statechange', () => {
      if (waiting.state === 'activated') reloadOnce();
    });

    // iOS suspends SW processes aggressively and can drop a single postMessage to a
    // not-yet-running waiting worker — the old one-shot send is why the banner used to
    // reappear after "updating". Retry until it activates or we give up.
    const send = () => { try { waiting.postMessage({ type: 'SKIP_WAITING' }); } catch (_) {} };
    send();
    let tries = 0;
    const retry = setInterval(() => {
      if (!window.__SW_USER_REQUESTED_RELOAD || waiting.state !== 'installed' || ++tries >= 10) {
        clearInterval(retry);
        return;
      }
      send();
    }, 800);
  }).catch(reloadOnce);

  // Last-resort watchdog so the user is never stuck on a logged-out dead screen.
  // If this fires with the update still unapplied, the banner will correctly reappear.
  setTimeout(reloadOnce, 10000);
}

/**
 * If a newer SW is already installed and waiting (e.g. the user missed the "Update available"
 * banner), activate it now. Callers use this to self-heal init failures caused by a stale SW
 * still serving a previously built cached asset, instead of surfacing a dead-end error.
 */
export function applyUpdateIfWaiting() {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
  navigator.serviceWorker.getRegistration()
    .then((reg) => { if (reg?.waiting) applyUpdate(); })
    .catch(() => {});
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(regs => {
      regs.forEach(reg => reg.unregister());
    });
  }
}